/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
*/

import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable } from '@angular/core';

/**
 * Angular service that stores the state/ Data.
 */
@Injectable()
export class BulbCKEStateService {
  /**
   * Event handling for Asset upload from desktop for showing upload progress.
   */
  assetLibraryUploadEvent: Subject<CustomEvent> = new Subject();

  /**
   * When the page has left align elements this flag is set true.
   */
  isEventsRegisterForMarginToCkeLists: boolean = false;

  /**
   * Orientation for Image carousel.
   */
  imageCarouselOrientation: BehaviorSubject<string> = new BehaviorSubject('');

  /**
   * Event handling for Image carousel upload complete.
   */
  imageCarouselUploadCompleteEvent: Subject<CustomEvent> = new Subject();

  /**
   * Event handling for Image carousel crop upload complete.
   */
  imageCarouselCropUploadCompleteEvent: Subject<CustomEvent> = new Subject();

  /**
   * Event handle scrolling to the new inserted paragraph once image upload completes.
   */
  insertParagraphSubject : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Event handle scrolling to the new inserted paragraph once table insertion completes.
   */
  insertParagraphAfterTableSubject : BehaviorSubject<any> = new BehaviorSubject<any>(null);

  /**
   * Event handle cancellation of update api call. we need to cancel all update operations
   * when asset is uploaded but not rendered yet.
   */
  handleSaveOperation : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * Event handle captured splash image url for instant play video.
   */
  capturedSplashUrl : BehaviorSubject<any> = new BehaviorSubject<any>('');

  /**
   * Event handle data needed to start transcoding for instant play.
   */
  startTranscoding : BehaviorSubject<any> = new BehaviorSubject<any>('');

  /**
   * Event handle comment toggle to show/hide comments.
   */
  isCommentEnabledSubject : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * Event handle busy indicator during upload.
   */
  stopBusyIndicator : BehaviorSubject<any> = new BehaviorSubject<any>(null);

  /**
   * Asset type
   */
  assetType : BehaviorSubject<any> = new BehaviorSubject<any>('IMAGE');

  /**
   * upload to server
   */
  uploadToServer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * This is used to track current file number, In case of multiple file upload.
   */
  multipleUploadStart: BehaviorSubject<number> = new BehaviorSubject<number>(-1);

  /**
   * Whether the asset is uploaded from desktop.
   * @private
   */
  private isDesktopUploadSelectionMode: boolean;

  /**
   * Whether Image carousel is added from media.
   * @private
   */
  private isImageCarouselCreation: boolean;

  /**
   * Whether Image carousel is added from media.
   * @private
   */
  private multipleImage: boolean = false;

  /**
   * Returns the value of isDesktopUploadSelectionMode
   */
  public getDesktopUploadSelectionMode(): boolean {
    return this.isDesktopUploadSelectionMode;
  }

  /**
   * Set value for isDesktopUploadSelectionMode.
   * @param status boolean, specifies whether current mode
   * of asset upload is Desktop.
   */
  public setDesktopUploadSelectionMode(status: boolean) {
    this.isDesktopUploadSelectionMode = status;
  }

  /**
   * Returns the value of isImageCarouselCreation
   */
  public getImageCarouselCreation(): boolean {
    return this.isImageCarouselCreation;
  }

  /**
   * Set value for isImageCarouselCreation
   * @param status boolean, specifies whether Image carousel
   * is uploaded from Media options.
   */
  public setImageCarouselCreation(status: boolean) {
    this.isImageCarouselCreation = status;
  }

  /**
   * Initialize the value of imageCarouselUploadCompleteEvent.
   */
  public initializeImageCarouselUploadEvent() {
    this.imageCarouselUploadCompleteEvent = new Subject<CustomEvent>();
  }

  /**
   * Initialize the value of assetLibraryUploadEvent.
   */
  public initializeAssetLibraryUploadEvent() {
    this.assetLibraryUploadEvent = new Subject<CustomEvent>();
  }

  public isMultipleImage(): boolean {
    return this.multipleImage;
  }

  public setMultipleImage(value: boolean) {
    this.multipleImage = value;
  }
}
